import {
  type DateValue,
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  startOfMonth,
  startOfWeek,
  today,
} from "@internationalized/date";
import type { DateRangePreset } from "@/components/application/date-picker/date-range-calendar-popover";

/**
 * Generate standard date range presets for date pickers
 * @param locale - Locale string for week start day calculation (e.g., "en-US")
 * @param limits - Optional date limits for "All Time" preset
 * @param formatter - Optional date formatter for "All Time" subtext
 * @returns Record of preset keys to DateRangePreset objects
 */
export function generateDateRangePresets(
  locale: string,
  limits?: { minDate: DateValue; maxDate: DateValue },
  formatter?: Intl.DateTimeFormat,
): Record<string, DateRangePreset> {
  const now = today(getLocalTimeZone());

  const basePresets: Record<string, DateRangePreset> = {
    today: { label: "Today", value: { start: now, end: now } },
    yesterday: { label: "Yesterday", value: { start: now.subtract({ days: 1 }), end: now.subtract({ days: 1 }) } },
    thisWeek: { label: "This week", value: { start: startOfWeek(now, locale), end: endOfWeek(now, locale) } },
    lastWeek: {
      label: "Last week",
      value: {
        start: startOfWeek(now, locale).subtract({ weeks: 1 }),
        end: endOfWeek(now, locale).subtract({ weeks: 1 }),
      },
    },
    thisMonth: { label: "This month", value: { start: startOfMonth(now), end: endOfMonth(now) } },
    lastMonth: {
      label: "Last month",
      value: {
        start: startOfMonth(now).subtract({ months: 1 }),
        end: endOfMonth(now).subtract({ months: 1 }),
      },
    },
  };

  // Add "All Time" preset if limits are available
  if (limits && formatter) {
    const allTimeStart = formatter.format(limits.minDate.toDate(getLocalTimeZone()));
    return {
      ...basePresets,
      allTime: {
        label: "All Time",
        subtext: `From ${allTimeStart}`,
        value: { start: limits.minDate, end: limits.maxDate },
      },
    };
  }

  return basePresets;
}
